import { sectionRenderer } from '@theme/section-renderer';

const SIDEBAR_BREAKPOINT = window.matchMedia('(min-width: 750px)');

const initialiseCarousel = (root) => {
  if (!root || root.dataset.tigerReady === 'true') return;
  root.dataset.tigerReady = 'true';

  const slides = [...root.querySelectorAll('[data-tiger-slide]')];
  const dots = [...root.querySelectorAll('[data-tiger-dot]')];
  const status = root.querySelector('[data-tiger-status]');
  if (!slides.length) return;
  let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

  const loadSlideImage = (slide) => {
    const image = slide?.querySelector('img[data-tiger-src]');
    if (!image) return;
    image.src = image.dataset.tigerSrc;
    image.removeAttribute('data-tiger-src');
  };

  const show = (next, focus = false) => {
    active = (next + slides.length) % slides.length;
    loadSlideImage(slides[active]);
    slides.forEach((slide, index) => {
      const selected = index === active;
      slide.classList.toggle('is-active', selected);
      slide.setAttribute('aria-hidden', String(!selected));
      slide.querySelectorAll('a, button').forEach((control) => {
        if (!selected) control.setAttribute('tabindex', '-1');
        else control.removeAttribute('tabindex');
      });
    });
    dots.forEach((dot, index) => {
      const selected = index === active;
      dot.classList.toggle('is-active', selected);
      dot.setAttribute('aria-current', selected ? 'true' : 'false');
    });
    if (status) status.textContent = `Slide ${active + 1} of ${slides.length}`;
    if (focus) slides[active]?.querySelector('a')?.focus({ preventScroll: true });
  };

  root.querySelector('[data-tiger-prev]')?.addEventListener('click', () => show(active - 1));
  root.querySelector('[data-tiger-next]')?.addEventListener('click', () => show(active + 1));
  dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
  root.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') show(active - 1);
    if (event.key === 'ArrowRight') show(active + 1);
  });
  show(active);
};

const getResultsList = () => document.querySelector('results-list[section-id]');
const getDesktopSidebar = () => getResultsList()?.querySelector('.facets-block-wrapper--vertical');

const setSidebarState = (root, expanded) => {
  const toggle = root.querySelector('[data-tiger-sidebar-toggle]');
  const label = root.querySelector('[data-tiger-sidebar-label]');
  const sidebar = getDesktopSidebar();
  if (!toggle || !sidebar) return;

  sidebar.id = 'TigerCatalogueFilters';
  sidebar.classList.toggle('hidden', !expanded);
  toggle.setAttribute('aria-expanded', String(expanded));
  if (label) label.textContent = expanded ? 'Hide filters' : 'Filters';
  root.dataset.tigerSidebarOpen = String(expanded);
};

const readProductCount = () => {
  const facets = getResultsList()?.querySelector('facets-form-component[data-results-count]');
  const count = Number(facets?.dataset.resultsCount);
  return Number.isFinite(count) ? count : null;
};

const updateCatalogueHeader = (root, activeLink) => {
  const label = activeLink?.dataset.tigerRangeLabel || 'Products';
  root.querySelectorAll('[data-tiger-range-link]').forEach((link) => {
    if (link === activeLink) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  });

  const kicker = root.querySelector('[data-tiger-catalogue-kicker]');
  if (kicker) kicker.textContent = label;

  const count = readProductCount() ?? Number(activeLink?.dataset.tigerRangeCount);
  const countElement = root.querySelector('[data-tiger-product-count]');
  if (countElement && Number.isFinite(count)) countElement.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;

  const status = root.querySelector('[data-tiger-range-status]');
  if (status) status.textContent = `${label} products loaded.`;
};

const renderRange = async (root, targetURL, activeLink, { push = true } = {}) => {
  const resultsList = getResultsList();
  const sectionId = resultsList?.getAttribute('section-id');
  if (!sectionId) {
    window.location.assign(targetURL.toString());
    return;
  }

  const sidebarOpen = root.dataset.tigerSidebarOpen !== 'false';
  const requestId = String((Number(root.dataset.tigerRequestId) || 0) + 1);
  root.dataset.tigerRequestId = requestId;
  root.classList.add('is-loading');
  root.setAttribute('aria-busy', 'true');

  try {
    const renderURL = new URL(targetURL.toString());
    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: 'full',
      url: renderURL,
    });
    if (root.dataset.tigerRequestId !== requestId) return;
    if (push) history.pushState({ tigerRange: true }, '', targetURL);
    updateCatalogueHeader(root, activeLink);
    setSidebarState(root, sidebarOpen);
    document.dispatchEvent(new CustomEvent('tiger:collection-updated', { detail: { url: targetURL.toString() } }));
  } catch (error) {
    console.error('Tiger range update failed; using normal navigation.', error);
    window.location.assign(targetURL.toString());
  } finally {
    if (root.dataset.tigerRequestId === requestId) {
      root.classList.remove('is-loading');
      root.removeAttribute('aria-busy');
    }
  }
};

const initialiseRangeFilter = (root) => {
  if (!root || root.dataset.tigerRangeReady === 'true') return;
  root.dataset.tigerRangeReady = 'true';
  root.dataset.tigerSidebarOpen = 'true';

  root.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-tiger-sidebar-toggle]');
    if (toggle) {
      setSidebarState(root, toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const link = event.target.closest('[data-tiger-range-link]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const targetURL = new URL(link.href, window.location.href);
    if (targetURL.origin !== window.location.origin) return;
    event.preventDefault();
    renderRange(root, targetURL, link);
  });

  root.addEventListener('pointerenter', (event) => {
    const link = event.target.closest?.('[data-tiger-range-link]');
    const sectionId = getResultsList()?.getAttribute('section-id');
    if (!link || !sectionId) return;
    sectionRenderer.getSectionHTML(sectionId, true, new URL(link.href, window.location.href)).catch(() => {});
  }, true);

  setSidebarState(root, SIDEBAR_BREAKPOINT.matches);
};

const initialise = (scope = document) => {
  scope.querySelectorAll('[data-tiger-carousel]').forEach(initialiseCarousel);
  scope.querySelectorAll('[data-tiger-range-filter]').forEach(initialiseRangeFilter);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
else initialise();

document.addEventListener('shopify:section:load', (event) => initialise(event.target));
window.addEventListener('popstate', () => {
  const root = document.querySelector('[data-tiger-range-filter]');
  if (!root) return;
  const activeLink = [...root.querySelectorAll('[data-tiger-range-link]')].find((link) => {
    const linkURL = new URL(link.href, window.location.href);
    return linkURL.pathname === window.location.pathname;
  });
  renderRange(root, new URL(window.location.href), activeLink, { push: false });
});
